import axios from "axios";
import { useMutation, useQuery } from "@tanstack/react-query";

import { __API_URL__ } from "../SERVER_URL";
import { TYPES_OF_REPORTS } from "../utils/constants";

const startReportGeneration = async (reportType, payload) => {
  const token = localStorage.getItem("token");

  const REPORT_DOWNLOAD_URLS = {
    [TYPES_OF_REPORTS.POEM]: "/api/reports/start_poem_report/",
    [TYPES_OF_REPORTS.ADVANCED_SURVEY]: "/api/reports/start_advanced_surveys_report/",
    [TYPES_OF_REPORTS.EDIARY]: "/api/reports/start_ediary_report/",
    [TYPES_OF_REPORTS.AI_SCORE]: "/api/reports/start_ai_score_report/",
    [TYPES_OF_REPORTS.APP_PHOTO_SCORE]: "/api/reports/start_app_photo_score_report/",
    [TYPES_OF_REPORTS.EXTERNAL_PHOTO_SCORE]: "/api/reports/start_external_photo_score_report/",
    [TYPES_OF_REPORTS.ECRF]: "/api/reports/start_ecrf_report/",
    [TYPES_OF_REPORTS.ECRF_BLOCKWISE]: "/api/reports/start_ecrf_report/",
    [TYPES_OF_REPORTS.PHOTO]: "/api/reports/start_photo_report/",
    [TYPES_OF_REPORTS.EXTERNAL_PHOTOS]: "/api/reports/start_external_photo_report/",
    [TYPES_OF_REPORTS.EXTERNAL_VIDEOS]: "/api/reports/start_external_video_report/",
    [TYPES_OF_REPORTS.VISIT]: "/api/reports/start_visit_report/",
    [TYPES_OF_REPORTS.EXTERNAL_DOCUMENTS]: "/api/reports/start_external_documents_report/",
    [TYPES_OF_REPORTS.VIDEO]: "/api/reports/start_video_report/",
    [TYPES_OF_REPORTS.ECONSENT]: "/api/reports/start_econsent_report/",
    [TYPES_OF_REPORTS.ECRF_CHANGE_LOGS]: "/api/reports/start_ecrf_change_logs_report/",
    [TYPES_OF_REPORTS.QUERY_CHANGE_LOGS]: "/api/reports/start_query_change_logs_report/",
    [TYPES_OF_REPORTS.ECRF_DISCREPANCY]: "/api/reports/start_ecrf_discrepancy_report/",
  };

  const reportUrl = REPORT_DOWNLOAD_URLS[reportType];
  const url = `${__API_URL__}${reportUrl}`;

  const response = await axios.post(url, payload, {
    headers: {
      Authorization: `Token ${token}`,
      Timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    },
    validateStatus: (s) => s === 202 || (s >= 200 && s < 300),
  });

  if (response.status !== 202) {
    throw new Error(response.data?.detail || "Failed to enqueue report job");
  }

  const jobId = response.data?.job_id;
  if (!jobId) throw new Error("Backend did not return job_id");

  const statusUrl = `${__API_URL__}/api/reports/${jobId}/status`;
  const rowsUrlBase = `${__API_URL__}/api/reports/${jobId}/rows`;

  return { jobId, statusUrl, rowsUrlBase };
};

const startDownloadZip = async (reportType, jobId, exportRequestId) => {
  const token = localStorage.getItem("token");

  const REPORT_DOWNLOAD_URLS = {
    [TYPES_OF_REPORTS.EXTERNAL_PHOTOS]: "/api/reports/start_external_photo_zip/",
    [TYPES_OF_REPORTS.EXTERNAL_VIDEOS]: "/api/reports/start_external_video_zip/",
    [TYPES_OF_REPORTS.PHOTO]: "/api/reports/start_photo_zip/",
    [TYPES_OF_REPORTS.VIDEO]: "/api/reports/start_video_zip/",
    [TYPES_OF_REPORTS.EXTERNAL_DOCUMENTS]: "/api/reports/start_external_document_zip/",
    [TYPES_OF_REPORTS.ECONSENT]: "/api/reports/start_econsent_zip/",
  };

  const reportUrl = REPORT_DOWNLOAD_URLS[reportType];
  const url = `${__API_URL__}${reportUrl}${jobId}/`;

  // exportRequestId is only present for the Export Study flow (undefined for Ad Hoc Report) —
  // the backend can't derive it from the manifest job's own FK anymore, since it's cleared the
  // moment the manifest succeeds (see handle_manifest_phase_completion on the backend).
  const response = await axios.post(
    url,
    exportRequestId ? { study_export_request_id: exportRequestId } : null,
    {
      headers: {
        Authorization: `Token ${token}`,
        Timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      },
      validateStatus: (s) => s === 202 || (s >= 200 && s < 300),
    }
  );

  if (response.status !== 202) {
    throw new Error(response.data?.detail || "Failed to enqueue report zip job");
  }

  const jobIdReturned = response.data?.job_id;
  if (!jobIdReturned) throw new Error("Backend did not return job_id");

  const statusUrl = `${__API_URL__}/api/reports/${jobIdReturned}/status`;

  return { jobId: jobIdReturned, statusUrl };
};

export const getReportStatus = async ({ statusUrl, signal }) => {
  const token = localStorage.getItem("token");

  const url = statusUrl.startsWith("http") ? statusUrl : `${__API_URL__}${statusUrl}`;
  const res = await axios.get(url, {
    signal,
    headers: {
      Authorization: `Token ${token}`,
    },
  });

  return res.data;
};

export const getReportRows = async ({ rowsUrlBase, limit = 100, offset = 0, signal }) => {
  const token = localStorage.getItem("token");

  const url = rowsUrlBase.startsWith("http")
    ? `${rowsUrlBase}?limit=${limit}&offset=${offset}`
    : `${__API_URL__}${rowsUrlBase}?limit=${limit}&offset=${offset}`;

  try {
    const res = await axios.get(url, {
      signal,
      headers: {
        Authorization: `Token ${token}`,
      },
    });

    return res.data;
  } catch (err) {
    throw err;
  }
};

export const useStartReportGenerationMutation = () =>
  useMutation({
    mutationFn: ({ reportType, payload }) => startReportGeneration(reportType, payload),
  });

export const useStartDownloadZipMutation = () =>
  useMutation({
    mutationFn: ({ reportType, jobId, exportRequestId }) =>
      startDownloadZip(reportType, jobId, exportRequestId),
  });

export const useReportStatusQuery = (statusUrl, { enabled = false } = {}) =>
  useQuery({
    queryKey: ["report-status", statusUrl],
    enabled: enabled && !!statusUrl,

    queryFn: ({ signal }) => getReportStatus({ statusUrl, signal }),

    refetchInterval: (q) => {
      const s = q?.state?.data?.status;
      if (s === "SUCCESS" || s === "FAILED") return false;
      return 1000; // poll every 1s until done
    },
    refetchIntervalInBackground: true,
    refetchOnWindowFocus: false,
    staleTime: 0,
  });

export const useVisitReportRowsQuery = ({ rowsUrlBase, limit = 50, offset = 0, enabled = true }) =>
  useQuery({
    queryKey: ["report-rows", rowsUrlBase, limit, offset],
    enabled: enabled && !!rowsUrlBase,
    refetchOnWindowFocus: false,
    retry: false,
    queryFn: ({ signal }) => getReportRows({ rowsUrlBase, limit, offset, signal }),
  });
